import { useMutation, useQueryClient } from "react-query";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import CustomFormsDataService from "../../services/custom-forms-data-service/CustomFormsDataService";
import { RestError } from "../../services/rest-service/RestService";
import { IFormInstanceData } from "../../views/object-form-view/Models";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";

import { keys } from "./Keys";

type MutationContextType = {
    previousInstances: IFormInstanceData[] | undefined;
};

export type UseDeleteCustomFormInstanceParams = {
    customerID: number,
    groupID: number,
    objectID: number,
    objectType: eLynxObjectTypeEnum;
    startTime: Date | undefined,
    endTime: Date | undefined,
    offline: boolean
} & BaseUseMutationParameters<string, IFormInstanceData>;

export function useDeleteCustomFormInstance({
    customerID,
    groupID,
    objectID,
    objectType,
    startTime,
    endTime,
    offline,
    onError,
    onSuccess
}: UseDeleteCustomFormInstanceParams) {
    const queryClient = useQueryClient();

    return useMutation<string, RestError, IFormInstanceData, MutationContextType>(
        (formInstance) => {
            return CustomFormsDataService.deleteInstance(
                customerID,
                groupID,
                formInstance,
                offline ?? false
            );
        },
        {
            onMutate: async (item: IFormInstanceData) => {

                await queryClient.cancelQueries(keys.all);

                const previousInstances = queryClient.getQueryData<IFormInstanceData[]>(
                    keys.list(
                        customerID,
                        groupID,
                        objectID,
                        objectType,
                        offline ?? false,
                        startTime,
                        endTime
                    )
                );

                queryClient.setQueryData<IFormInstanceData[]>(
                    keys.list(
                        customerID,
                        groupID,
                        objectID,
                        objectType,
                        offline ?? false,
                        startTime,
                        endTime
                    ),
                    (old?: IFormInstanceData[]) => {
                        return old?.filter(t => t.id !== item.id) ?? [];
                    }
                );

                return {
                    previousInstances
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _runTicket, context) => {

                if (error && context?.previousInstances) {
                    queryClient.setQueryData<IFormInstanceData[]>(
                        keys.list(
                            customerID,
                            groupID,
                            objectID,
                            objectType,
                            offline ?? false,
                            startTime,
                            endTime
                        ),
                        context?.previousInstances
                    );
                }

                queryClient.invalidateQueries(keys.lists());
            }
        }
    );
}
